import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw } from "lucide-react";
import Footer from "../components/Footer";
import { api, fmtPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const QUESTIONS = [
  {
    q: "What is your primary research focus?",
    key: "focus",
    options: [
      { label: "Tissue repair & recovery", value: "Recovery" },
      { label: "Metabolic & glucose regulation", value: "Metabolic" },
      { label: "Growth hormone / GH axis", value: "Growth" },
      { label: "Neurological & cognition", value: "Neuro" },
      { label: "Skin, longevity & cellular", value: "Skin & Longevity" },
    ],
  },
  {
    q: "What matters most for your protocol?",
    key: "priority",
    options: [
      { label: "Maximum potency", value: "potency" },
      { label: "Well-documented compounds", value: "documented" },
      { label: "Budget efficiency", value: "budget" },
    ],
  },
  {
    q: "Do you need reconstitution supplies?",
    key: "supplies",
    options: [
      { label: "Yes, include bacteriostatic water", value: "yes" },
      { label: "No, compounds only", value: "no" },
    ],
  },
];

export default function Quiz() {
  const { addItem } = useCart();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const choose = async (opt) => {
    const next = { ...answers, [QUESTIONS[step].key]: opt.value };
    setAnswers(next);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        const { data } = await api.get("/products", { params: { category: next.focus } });
        let list = [...data];
        if (next.priority === "potency") list.sort((a, b) => b.price - a.price);
        else if (next.priority === "budget") list.sort((a, b) => a.price - b.price);
        else list.sort((a, b) => (b.featured === true) - (a.featured === true));
        let picks = list.slice(0, 3);
        if (next.supplies === "yes") {
          const { data: water } = await api.get("/products/bac-water").catch(() => ({ data: null }));
          if (water) picks = [...picks, water];
        }
        setResults(picks);
      } catch { toast.error("Something went wrong. Try again."); }
      finally { setLoading(false); }
    }
  };

  const restart = () => { setStep(0); setAnswers({}); setResults(null); };
  const progress = results ? 100 : (step / QUESTIONS.length) * 100;

  return (
    <div className="pt-20 min-h-screen flex flex-col bg-[#0F0F10]">
      <section className="mx-auto max-w-[900px] w-full px-6 sm:px-10 py-16 flex-1">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#4ADE80] mb-5 text-center">Research Finder</p>
        <h1 className="font-serif-editorial text-4xl md:text-6xl text-[#F5F5F5] text-center leading-[1.02]">Find the Right<br />Research Compound.</h1>

        <div className="mt-10 h-px bg-white/10 relative">
          <div className="absolute inset-y-0 left-0 bg-[#4ADE80] transition-[width] duration-500" style={{ width: `${progress}%` }} />
        </div>

        <div className="mt-12" data-testid="quiz-container">
          <AnimatePresence mode="wait">
            {results ? (
              <motion.div key="results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <h2 className="font-display text-2xl text-[#F5F5F5] text-center">Recommended for your research</h2>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {results.map((p) => (
                    <div key={p.slug} data-testid={`quiz-result-${p.slug}`} className="flex gap-4 bg-[#1F1F1F] border border-white/[0.06] p-4">
                      <img src={p.image} alt={p.name} className="h-24 w-24 object-cover bg-[#0F0F10] border border-white/10" />
                      <div className="flex-1 min-w-0">
                        <Link to={`/products/${p.slug}`}><h3 className="font-display text-[15px] text-[#F5F5F5] hover:text-[#4ADE80] transition-colors">{p.name}</h3></Link>
                        <p className="text-[12px] text-[#A8A8A8]">{p.size}</p>
                        <div className="mt-3 flex items-center justify-between">
                          <span className="font-display text-sm">{fmtPrice(p.price)}</span>
                          <button onClick={() => { addItem(p, 1); toast.success(`${p.name} added`); }} className="btn-press text-[10px] uppercase tracking-[0.14em] text-[#0F0F10] bg-[#4ADE80] px-3 py-1.5 hover:bg-[#6FE998]">Add</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-10 flex justify-center gap-4">
                  <button data-testid="quiz-restart-btn" onClick={restart} className="btn-press border border-white/20 text-[#F5F5F5] uppercase tracking-[0.16em] text-[11px] px-6 py-3.5 hover:border-[#4ADE80] hover:text-[#4ADE80] inline-flex items-center gap-2"><RotateCcw className="h-3.5 w-3.5" /> Retake</button>
                  <Link to="/products" className="btn-press bg-[#4ADE80] text-[#0F0F10] uppercase tracking-[0.16em] text-[11px] px-6 py-3.5 hover:bg-[#6FE998] inline-flex items-center gap-2">Full Catalog <ArrowRight className="h-3.5 w-3.5" /></Link>
                </div>
              </motion.div>
            ) : (
              <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.4 }}>
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#7a7a7a] text-center mb-6">Question {step + 1} of {QUESTIONS.length}</p>
                <h2 className="font-serif-editorial text-3xl md:text-4xl text-[#F5F5F5] text-center">{QUESTIONS[step].q}</h2>
                <div className="mt-10 grid grid-cols-1 gap-3 max-w-lg mx-auto">
                  {QUESTIONS[step].options.map((opt) => (
                    <button key={opt.value} data-testid={`quiz-option-${opt.value}`} disabled={loading} onClick={() => choose(opt)}
                      className="btn-press text-left border border-white/12 px-6 py-5 text-[#E5E5E5] hover:border-[#4ADE80] hover:bg-white/[0.03] transition-[border-color,background-color] disabled:opacity-50">
                      {opt.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
      <Footer />
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
